import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { TimezoneSelection } from '@/components/ui/time-picker/TimezoneSelection';
import { allCustomerQuery } from '@/features/Site/graphql/query';
import { Customer, Device, DeviceByPkQuery } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { FeatureName } from '@/models/role.models';
import { SearchParam } from '@/models/search-param.models';
import hasPermission from '@/utils/permission.function';
import { hasNonEmptyField } from '@/utils/search-param.function';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import {
  Columns2,
  Download,
  LayoutGrid,
  List,
  ListPlus,
  MoreHorizontalIcon,
  ScanQrCode,
  UserPlus,
  X,
} from 'lucide-react';
import { DateTime } from 'luxon';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { Combobox } from '../../../components/Combobox';
import { AddDevicesDialog } from './AddDevicesDialog';
import { BulkAssignUserDialog } from './BulkAssignUserDialog';
import { QRScannerDialog } from './QRScannerDialog';

export type DeviceFilter = {
  name?: string | null;
  site?: string | null;
  serial?: string | null;
  customer?: string | null;
  environment?: string | null;
  status?: string[] | null;
  product?: string[] | null;
};

export type ViewMode = 'grid' | 'list';

type ListDeviceHeaderProps = {
  table: Table<Device>;
  viewMode: ViewMode;
  filters?: DeviceFilter;
  setFilters: Dispatch<SetStateAction<DeviceFilter | undefined>>;
  selectedTimezone?: string;
  onTimezoneChange?: (timezone?: string) => void;
  refetchDevices: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<DeviceByPkQuery, Error>>;
};

export default function ListDeviceHeader({
  table,
  filters,
  setFilters,
  refetchDevices,
  viewMode,
  selectedTimezone,
  onTimezoneChange,
}: ListDeviceHeaderProps) {
  const [addDevicesDialogOpen, setAddDevicesDialogOpen] = useState(false);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [qrScannerDialogOpen, setQrScannerDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);

  const showResetFiltersButton = hasNonEmptyField(filters);

  const { data: allCustomersData } = useQuery({
    queryKey: ['All Customers'],
    queryFn: () =>
      execute({
        query: allCustomerQuery,
      }),
    select: (data) => data,
    staleTime: 30000,
  });

  useEffect(() => {
    if (allCustomersData) {
      setCustomers(allCustomersData?.customer as Customer[]);
    }
  }, [allCustomersData]);

  const handleNameFilterValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setFilters({ ...filters, name: event.target.value });
  };

  const handleSiteFilterValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setFilters({ ...filters, site: event.target.value });
  };

  const handleSerialFilterValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setFilters({ ...filters, serial: event.target.value });
  };

  const handleClickResetFilters = () => {
    setFilters({});
  };

  const handleCustomerSelectionChange = (value: string) => {
    setFilters({ ...filters, customer: value === 'all' ? undefined : value });
  };

  const handleEnvironmentSelectionChange = (value: string) => {
    setFilters({
      ...filters,
      environment: value === 'all' ? undefined : value,
    });
  };

  const handleStatusSelectionChange = (value: string[]) => {
    setFilters({
      ...filters,
      status: value.length > 0 ? value : [],
    });
  };

  const handleQrScannerDialogOpen = (code: string | undefined) => {
    if (code) {
      setFilters({ ...filters, serial: code });
    }
    setQrScannerDialogOpen(!qrScannerDialogOpen);
  };

  const handleAddDevicesDialogOpen = async (shouldFetch?: boolean) => {
    if (shouldFetch) {
      await refetchDevices();
    }
    setAddDevicesDialogOpen(!addDevicesDialogOpen);
  };

  const handleBulkAssignDialogClose = async (shouldFetch?: boolean) => {
    if (shouldFetch) {
      await refetchDevices();
    }
    setBulkAssignDialogOpen(false);
  };

  // The rows currently rendered, i.e. one page of the filtered results.
  const pageDevices = table.getRowModel().rows.map((row) => row.original);

  const handleProductSelectionChange = (value: string[]) => {
    setFilters({
      ...filters,
      product: value.length > 0 ? value : [],
    });
  };

  const handleExportCSV = () => {
    const visibleColumns = table
      .getAllColumns()
      .filter((col) => col.getIsVisible() && col.id !== 'action');

    const headers = visibleColumns.map((col) => {
      return col.id;
    });

    const csvRows = table.getFilteredRowModel().rows.map((row) =>
      visibleColumns.map((col) => {
        const text = col.columnDef.meta?.exportValue?.(row.original) ?? '';
        return `"${text.replace(/"/g, '""')}"`;
      }),
    );

    const csv = [headers, ...csvRows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `devices_${DateTime.now().toFormat('yyyy-MM-dd_HH-mm')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-[repeat(8,_1fr)_auto]">
        <div>
          <Input
            placeholder="Filter by name or id..."
            name="deviceName"
            onChange={handleNameFilterValueChange}
            value={filters?.name ?? ''}
          />
        </div>

        <div className="relative">
          <Input
            name="serial"
            className="pr-8"
            placeholder="Filter by serial..."
            onChange={handleSerialFilterValueChange}
            value={filters?.serial ?? ''}
          />

          <Button
            className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
            size={'icon'}
            variant={'link'}
            onClick={() => setQrScannerDialogOpen(true)}
          >
            <ScanQrCode className="size-5" />
          </Button>
        </div>
        <div>
          <Input
            name="site"
            placeholder="Filter by site..."
            onChange={handleSiteFilterValueChange}
            value={filters?.site ?? ''}
          />
        </div>
        <div>
          <Select
            value={filters?.customer ?? ''}
            onValueChange={handleCustomerSelectionChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all" disabled={!filters?.customer}>
                  All
                </SelectItem>
                {customers.map((customer, key) => {
                  return (
                    <SelectItem value={customer.id} key={key}>
                      {customer.name}
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <Select
          value={filters?.environment ?? ''}
          onValueChange={handleEnvironmentSelectionChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Filter by env." />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={'all'} disabled={!filters?.environment}>
                All
              </SelectItem>
              <SelectItem value={'production'}>Production</SelectItem>
              <SelectItem value={'staging'}>Staging</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <Combobox
          selectedValues={filters?.status ?? []}
          onChange={handleStatusSelectionChange}
          placeholder="Filter by status"
          options={[
            {
              value: 'created',
              label: ' Created',
            },
            {
              value: 'installed',
              label: ' Installed',
            },
            {
              value: 'installationFailed',
              label: 'Install Failed',
            },
            {
              value: 'disabled',
              label: 'Disabled',
            },
          ]}
        />

        <Combobox
          selectedValues={filters?.product ?? []}
          onChange={handleProductSelectionChange}
          placeholder="Filter by product"
          options={[
            { value: 'senpic', label: 'Senpic' },
            { value: 'senshield', label: 'Senshield' },
            { value: 'senforce', label: 'Senforce' },
          ]}
        />
        <TimezoneSelection
          setTimezone={onTimezoneChange}
          timezone={selectedTimezone}
        />

        <div className="flex justify-start gap-2 md:col-auto 2xl:justify-end">
          {showResetFiltersButton && (
            <Button variant={'outline'} onClick={handleClickResetFilters}>
              <X />
              <span className="hidden md:inline">Reset</span>
            </Button>
          )}

          {viewMode === 'list' && (
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Columns2 />
                    {/* <span className="hidden md:inline"> Columns</span> */}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {table
                    ?.getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {column.id}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" aria-label="Open menu" size={'icon'}>
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>
                <Download />
                Export CSV
              </DropdownMenuItem>
              {hasPermission(FeatureName.CREATE_DEVICES) && (
                <DropdownMenuItem onClick={() => setAddDevicesDialogOpen(true)}>
                  <ListPlus />
                  Add Devices
                </DropdownMenuItem>
              )}
              {hasPermission(FeatureName.ASSIGN_USERS_TO_DEVICE) && (
                <DropdownMenuItem
                  disabled={pageDevices.length === 0}
                  onClick={() => setBulkAssignDialogOpen(true)}
                >
                  <UserPlus />
                  Assign User to Devices
                </DropdownMenuItem>
              )}
              {hasPermission(FeatureName.VIEW_DEVICES_GRID_MODE) && (
                <DropdownMenuItem
                  onClick={() => {
                    if (viewMode === 'grid') {
                      searchParams.delete(SearchParam.VIEW_MODE);
                      setSearchParams(searchParams, { replace: true });
                    } else {
                      searchParams.set(SearchParam.VIEW_MODE, 'grid');
                      setSearchParams(searchParams, { replace: true });
                    }
                  }}
                >
                  {viewMode === 'grid' ? (
                    <>
                      <List /> List View
                    </>
                  ) : (
                    <>
                      <LayoutGrid />
                      Grid View
                    </>
                  )}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {addDevicesDialogOpen && (
        <AddDevicesDialog
          customers={customers}
          onToggleOpen={(shouldFetch) => {
            void handleAddDevicesDialogOpen(shouldFetch);
          }}
          open={addDevicesDialogOpen}
        />
      )}
      {bulkAssignDialogOpen && (
        <BulkAssignUserDialog
          open={bulkAssignDialogOpen}
          devices={pageDevices}
          onToggleOpen={(shouldFetch) => {
            void handleBulkAssignDialogClose(shouldFetch);
          }}
        />
      )}
      {qrScannerDialogOpen && (
        <QRScannerDialog
          onToggleOpen={(code) => {
            void handleQrScannerDialogOpen(code);
          }}
          open={qrScannerDialogOpen}
        />
      )}
    </>
  );
}
